import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  CampaignOutlined as CampaignIcon,
  StorefrontOutlined as StorefrontIcon,
} from '@mui/icons-material'
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { useNotification } from '../contexts/NotificationContext'
import campaignApi, { Campaign } from '../services/campaignApi'
import ConfirmDialog from '../components/dialogs/ConfirmDialog'
import PageBanner from '../components/PageBanner'
import EmptyState from '../components/EmptyState'
import { TableSkeleton } from '../components/skeletons'
import { PAGE_BANNER_PRIMARY_BTN_SX } from '../theme/constants'
import SMBSelector from '../components/SMBSelector'
import { useSMBContext } from '../contexts/SMBContext'
import CampaignStatusChip from '../components/campaign/CampaignStatusChip'

const Campaigns: React.FC = () => {
  const { showNotification } = useNotification()
  const navigate = useNavigate()
  const outletContext = useOutletContext<{ smbNavState?: { isOnboarded: boolean; hasSubscription: boolean } } | null>()
  const hasSubscription = outletContext?.smbNavState?.hasSubscription
  const { smbs, selectedSMBId: smbId, setSelectedSMBId: setSmbId, loading: smbsLoading } = useSMBContext()
  const [loading, setLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null)

  // Load campaigns
  useEffect(() => {
    if (smbId) {
      loadCampaigns()
    }
  }, [smbId, statusFilter, searchQuery])

  // Reset search when SMB changes
  useEffect(() => {
    if (smbId) {
      setSearchQuery('')
    }
  }, [smbId])

  const loadCampaigns = async (isRefresh: boolean = false) => {
    if (!smbId) {
      setCampaigns([])
      setLoading(false)
      setIsRefreshing(false)
      return
    }
    try {
      if (isRefresh) {
        setIsRefreshing(true)
      } else {
        setLoading(true)
      }
      const filters: any = {}
      if (statusFilter) filters.status = statusFilter
      
      const response = await campaignApi.listCampaigns(smbId, filters)
      let filteredCampaigns = response.campaigns || []
      
      // Apply search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        filteredCampaigns = filteredCampaigns.filter((campaign: Campaign) => 
          campaign.name?.toLowerCase().includes(query) ||
          (campaign as any).template_name?.toLowerCase().includes(query)
        )
      }
      
      setCampaigns(filteredCampaigns)
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to load campaigns', 'error')
    } finally {
      setLoading(false)
      setIsRefreshing(false)
    }
  }

  const handleView = (id: string) => {
    navigate(`/campaigns/${id}`)
  }

  const handleEdit = (id: string) => {
    navigate(`/campaigns/${id}`)
  }

  const handleCancel = async (id: string) => {
    try {
      await campaignApi.cancelCampaign(id)
      showNotification('Campaign cancelled successfully', 'success')
      loadCampaigns()
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to cancel campaign', 'error')
    }
  }

  const handleDelete = (id: string) => {
    setCampaignToDelete(id)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!campaignToDelete) return
    try {
      await campaignApi.deleteCampaign(campaignToDelete)
      showNotification('Campaign deleted successfully', 'success')
      loadCampaigns()
    } catch (error: any) {
      showNotification(error.response?.data?.detail || 'Failed to delete campaign', 'error')
    }
    setDeleteDialogOpen(false)
    setCampaignToDelete(null)
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Campaign Name',
      minWidth: 250,
      width: 300,
      maxWidth: 450,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => handleView(params.row.id)}
          sx={{ 
            textTransform: 'none', 
            justifyContent: 'flex-start',
            fontWeight: 500,
            textAlign: 'left',
            padding: 0,
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {params.value || 'Untitled Campaign'}
        </Button>
      ),
    },
    {
      field: 'campaign_status',
      headerName: 'Status',
      minWidth: 150,
      width: 250,
      maxWidth: 300,
      renderCell: (params) => <CampaignStatusChip status={params.value} />,
    },
    {
      field: 'template_name',
      headerName: 'Template',
      minWidth: 180,
      width: 200,
      maxWidth: 300,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
          }}
          title={params.value || 'No template'}
        >
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'start_at',
      headerName: 'Scheduled Start',
      minWidth: 160,
      width: 180,
      maxWidth: 220,
      renderCell: (params) =>{{
  const value = params.value; // should be a full ISO string
  if (!value) return ""; // handle missing values
  const date = new Date(value);
  if (date.getTime() === 0) return "-";
  if (isNaN(date.getTime())) return "Invalid date";
  return date.toLocaleString();
}}
    },
    {
      field: 'end_at',
      headerName: 'End Date',
      minWidth: 160,
      width: 180,
      maxWidth: 220,
      renderCell: (params) =>{
        const value = params.value; // should be a full ISO string
  if (!value) return ""; // handle missing values
  const date = new Date(value);
  if (date.getTime() === 0) return "-";
  if (isNaN(date.getTime())) return "Invalid date";
  return date.toLocaleString();
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      minWidth: 100,
      width: 120,
      maxWidth: 150,
      getActions: (params) => {
        const campaign = params.row as Campaign
        const actions = [
          <GridActionsCellItem
            icon={<ViewIcon />}
            label="View"
            onClick={() => handleView(campaign.id)}
          />,
        ]

        if (campaign.campaign_status === 'draft') {
          actions.push(
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit"
              onClick={() => handleEdit(campaign.id)}
            />
          )
        }

        if (['scheduled', 'running', 'paused'].includes(campaign.campaign_status)) {
          actions.push(
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              onClick={() => handleCancel(campaign.id)}
            />
          )
        }


        // Delete button - not available when running
        if (campaign.campaign_status !== 'running') {
          actions.push(
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => handleDelete(campaign.id)}
              showInMenu
            />
          )
        }

        return actions
      },
    },
  ]

  return (
    <Box>
      {/* Header */}
      <PageBanner
        title="Campaigns"
        subtitle="Manage your WhatsApp broadcast campaigns"
        action={
          <Tooltip title={hasSubscription === false ? 'An Active Subscription is required to create campaigns' : ''}>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/campaigns/new')}
                disabled={loading || smbsLoading || !smbId || hasSubscription === false}
                sx={PAGE_BANNER_PRIMARY_BTN_SX}
              >
                Create Campaign
              </Button>
            </span>
          </Tooltip>
        }
        onRefresh={() => loadCampaigns(true)}
        refreshing={isRefreshing}
      />

      {/* SMB Selector */}
      <SMBSelector
        smbs={smbs}
        selectedSMBId={smbId}
        onChange={setSmbId}
      />

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search by name or template..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { height: 40, borderRadius: 1.5 }
              }}
            />

            <FormControl size="small" sx={{ minWidth: 160, flexGrow: 1 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ height: 40, borderRadius: 1.5 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="running">Running</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="partially_completed">Pending Retry</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="waiting_for_quota">Waiting For Quota</MenuItem>
                <MenuItem value="waiting_for_template_approval">Waiting For Template Approval</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Campaigns Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ height: 600, width: '100%' }}>
            {(smbs.length === 0 || (smbs.length === 1 && !smbs[0].onboarding_completed) || (smbId && !smbs.find((s) => s.id === smbId)?.onboarding_completed)) && !smbsLoading ? (
              hasSubscription === false ? (
                <EmptyState
                  icon={<StorefrontIcon />}
                  title="Subscription Required"
                  description="An active plan is required to access Campaigns. Choose a plan to start sending bulk broadcast messages to your leads."
                  action={
                    <Button variant="contained" onClick={() => navigate('/dashboard')}>
                      Choose Plan
                    </Button>
                  }
                  minHeight={400}
                />
              ) : (
                <EmptyState
                  icon={<StorefrontIcon />}
                  title="Add a Business to Get Started"
                  description="You haven't added a business yet. Connect your WhatsApp business to create campaigns."
                  action={
                    <Button variant="contained" onClick={() => navigate('/dashboard')}>
                      Add Business
                    </Button>
                  }
                  minHeight={400}
                />
              )
            ) : !smbId && !smbsLoading ? (
              <EmptyState
                icon={<StorefrontIcon />}
                title="No Business Selected"
                description="Select a business from the dropdown above to view campaigns."
                minHeight={400}
              />
            ) : (
              <DataGrid
                rows={campaigns}
                columns={columns}
                loading={(loading && !isRefreshing) || smbsLoading}
                pageSizeOptions={[10, 25, 50]}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10 },
                  },
                }}
                disableRowSelectionOnClick
                slots={{
                  loadingOverlay: () => <TableSkeleton variant="campaigns" />,
                  noRowsOverlay: () => (
                    <EmptyState
                      icon={<CampaignIcon />}
                      title={searchQuery || statusFilter ? 'No matching campaigns' : 'No campaigns created yet'}
                      description={
                        searchQuery || statusFilter
                          ? 'No campaigns match your active search or status filter. Try clearing your filters.'
                          : 'Reach out to your leads with automated WhatsApp broadcast campaigns.'
                      }
                      action={
                        !searchQuery && !statusFilter ? (
                          <Tooltip title={hasSubscription === false ? 'An active plan is required to create campaigns' : ''}>
                            <span>
                              <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/campaigns/new')}
                                disabled={hasSubscription === false}
                              >
                                Create Campaign
                              </Button>
                            </span>
                          </Tooltip>
                        ) : undefined
                      }
                      minHeight={400}
                    />
                  ),
                }}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    padding: '12px 16px',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f9fafb',
                    borderBottom: '2px solid rgba(224, 224, 224, 1)',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-row': {
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: '#fafafa',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  },
                  '& .MuiDataGrid-rowHeight': {
                    minHeight: '64px !important',
                  },
                }}
                onRowClick={(params) => handleView(params.id as string)}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Campaign"
        description={
          campaignToDelete
            ? `Are you sure you want to delete "${campaigns.find(c => c.id === campaignToDelete)?.name || 'this campaign'}"? This action cannot be undone.`
            : 'Are you sure you want to delete this campaign? This action cannot be undone.'
        }
        confirmLabel="Delete"
        onClose={() => {
          setDeleteDialogOpen(false)
          setCampaignToDelete(null)
        }}
        onConfirm={confirmDelete}
      />
    </Box>
  )
}

export default Campaigns

